import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function ChatTemplatesTab() {
  const [chatTemplates, setChatTemplates] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/chat/templates');
      setChatTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      // Use default templates if fetch fails
      setChatTemplates([
        { id: 'TPL001', name: 'Welcome Message', category: 'onboarding', content: 'Welcome to JustVerified! We are here to assist you with your background verification process.' },
        { id: 'TPL002', name: 'Document Request', category: 'verification', content: 'We require additional documents for your verification. Please upload the following: [Document List]' },
        { id: 'TPL003', name: 'Verification Complete', category: 'completion', content: 'Your background verification has been completed successfully. Thank you for your cooperation.' },
        { id: 'TPL004', name: 'Insufficiency Notice', category: 'insufficiency', content: 'We found some discrepancies in your submitted documents. Please provide clarification for: [Issue Details]' },
        { id: 'TPL005', name: 'Follow-up Reminder', category: 'reminder', content: 'This is a friendly reminder to submit your pending documents for the verification process.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleSaveTemplate = async () => {
    try {
      if (editingTemplate?.id && !editingTemplate.id.startsWith('TPL')) {
        // Update existing (local only for now)
        setChatTemplates(chatTemplates.map(t => t.id === editingTemplate.id ? editingTemplate : t));
      } else if (editingTemplate?.id) {
        // Backend update
        await axios.put(`/chat/templates/${editingTemplate.id}`, editingTemplate);
        fetchTemplates();
      } else {
        // Create new
        const response = await axios.post('/chat/templates', editingTemplate);
        setChatTemplates([...chatTemplates, response.data]);
      }
      toast.success('Template saved');
      setShowTemplateDialog(false);
    } catch (error) {
      // Fallback to local storage
      if (editingTemplate?.id) {
        setChatTemplates(chatTemplates.map(t => t.id === editingTemplate.id ? editingTemplate : t));
      } else {
        setChatTemplates([...chatTemplates, { ...editingTemplate, id: `TPL${Date.now()}` }]);
      }
      setShowTemplateDialog(false);
      toast.success('Template saved locally');
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await axios.delete(`/chat/templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      // Fallback to local deletion
      setChatTemplates(chatTemplates.filter(t => t.id !== id));
      toast.success('Template removed');
    }
  };

  return (
    <div data-testid="chat-templates-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Chat Templates</h1>
        <Button onClick={() => { setEditingTemplate({ name: '', category: 'general', content: '' }); setShowTemplateDialog(true); }}>
          <MessageSquare className="w-4 h-4 mr-2" />Add Template
        </Button>
      </div>
      
      {loading ? (
        <p className="text-center text-slate-500 py-8">Loading templates...</p>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {chatTemplates.map(template => (
            <Card key={template.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="font-semibold text-lg">{template.name}</p>
                      <Badge className="bg-slate-100 text-slate-700">{template.category}</Badge>
                    </div>
                    <p className="text-slate-600 text-sm">{template.content}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => { setEditingTemplate(template); setShowTemplateDialog(true); }}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteTemplate(template.id)}>
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {showTemplateDialog && (
        <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingTemplate?.id ? 'Edit Template' : 'Add Template'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Template Name</Label>
                <Input 
                  value={editingTemplate?.name || ''} 
                  onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                  placeholder="e.g., Welcome Message"
                />
              </div>
              <div>
                <Label>Category</Label>
                <select 
                  className="w-full border rounded-md p-2"
                  value={editingTemplate?.category || 'general'}
                  onChange={(e) => setEditingTemplate({...editingTemplate, category: e.target.value})}
                >
                  <option value="general">General</option>
                  <option value="onboarding">Onboarding</option>
                  <option value="verification">Verification</option>
                  <option value="completion">Completion</option>
                  <option value="insufficiency">Insufficiency</option>
                  <option value="reminder">Reminder</option>
                </select>
              </div>
              <div>
                <Label>Message Content</Label>
                <textarea 
                  className="w-full border rounded-md p-2 min-h-[100px]"
                  value={editingTemplate?.content || ''}
                  onChange={(e) => setEditingTemplate({...editingTemplate, content: e.target.value})}
                  placeholder="Enter template message..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveTemplate}>Save Template</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
